/**
 * @file: useExtendedPropertyModal.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useExtendedPropertyContext } from '@euler/app/flows/order/pages/OrderScreen/components/ExtendedPropertyOptionModal';
import { ExtendedOrderPropertyOption } from '@euler/model';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useCallback, useMemo } from 'react';
import { InteractionManager } from 'react-native';

export const useExtendedPropertyModal = () => {
  const { orderDetail$, updateDetail, formController } = useOrderFlowContext();
  const [{ extendedProps = [] }] = useBehaviorSubject(orderDetail$);
  const { property$ } = useExtendedPropertyContext();
  const [propertyDef] = useBehaviorSubject(property$);
  const visible = propertyDef != null && propertyDef.type === 'select';
  const options = useMemo(
    () => (visible ? propertyDef.options : []),
    [propertyDef, visible],
  );
  const property = extendedProps.find(x => x.id === propertyDef?.id);
  const selected = useMemo<string[]>(
    () =>
      property?.value
        ? typeof property.value === 'string'
          ? [property.value]
          : typeof property.value === 'boolean'
          ? []
          : property.value
        : [],
    [property],
  );

  const onSelect = usePersistFn((option: ExtendedOrderPropertyOption) => {
    if (propertyDef == null || propertyDef.type !== 'select') {
      return;
    }

    const id = propertyDef.id;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const value = option.value || option.defaultValue;

    updateDetail(draft => {
      if (!draft.extendedProps) {
        draft.extendedProps = [];
      }
      if (propertyDef.multi) {
        let target = draft.extendedProps.find(x => x.id === id);
        if (!target) {
          target = {
            id,
            value: [],
          };
          draft.extendedProps.push(target);
        }
        if (typeof target.value === 'boolean') {
          return;
        }
        if (typeof target.value === 'string') {
          target.value = [target.value];
        }
        if (target.value.includes(value)) {
          target.value = target.value.filter(x => x !== value);
        } else {
          target.value.push(value);
        }
      } else {
        const target = draft.extendedProps.find(x => x.id === id);
        if (!target) {
          draft.extendedProps.push({
            id,
            value,
          });
        } else {
          target.value = value;
        }
      }
    });

    if (propertyDef.required) {
      void InteractionManager.runAfterInteractions(() => {
        formController.form.extendedProperties.validate();
      });
    }
  });

  const onRemarkChange = usePersistFn((s: string) => {
    if (propertyDef == null || propertyDef.type !== 'select') {
      return;
    }

    const id = propertyDef.id;

    updateDetail(draft => {
      if (!draft.extendedProps) {
        draft.extendedProps = [];
      }
      let target = draft.extendedProps.find(x => x.id === id);
      if (!target) {
        target = {
          id,
          value: propertyDef.multi ? [] : '',
          remark: s,
        };
        draft.extendedProps.push(target);
      } else {
        target.remark = s;
      }
    });
  });

  const onDismiss = useCallback(() => {
    property$.next(undefined);
  }, [property$]);

  return {
    visible,
    propertyDef,
    property,
    options,
    selected,
    onSelect,
    onRemarkChange,
    onDismiss,
  };
};
